import { dispatchLayoutChange, tryExecution } from './utils';

import Modal from 'bootstrap/js/src/modal';
import { enableBlurBackdrop } from './bootstrap';
const baseClass = 'tile';
const EVENT_TILE_OPEN = 'tile-overlay-open';

const OVERLAY_TYPE_SMALL = 'small';
const OVERLAY_TYPE_BIG = 'big';
let modalCustomClasses = '';
let modalDialogClasses = '';

class Tile {
    /**
     * @private
     * @type {HTMLElement}
     */
    _el;

    /**
     * @type {"big"|"small"}
     * @private
     */
    _overlayType = OVERLAY_TYPE_SMALL;

    /**
     * @private
     * @type {boolean}
     */
    _isOpen = false;

    /**
     * @private
     * @type {HTMLElement|null}
     */
    _toggleButton;

    /**
     * @private
     * @type {HTMLButtonElement|null}
     */
    _closeButton = null;

    /**
     * @private
     * @type {HTMLTemplateElement|null}
     */
    _overlayTemplate;

    /**
     * @private
     * @type {HTMLElement|null}
     */
    _overlay = null;

    /**
     * @type {Modal|null}
     * @private
     */
    _overlayModal = null;

    /**
     * @param {HTMLElement} element
     */
    constructor(element) {
        this._el = element;

        this._toggleButton = this._el.getElementsByClassName(`${baseClass}__toggle`)[0] ?? null;
        this._overlayTemplate = this._el.getElementsByClassName(`${baseClass}__overlay-template`)[0] ?? null;

        if (this._el.classList.contains(`${baseClass}--video`)) {
            modalCustomClasses = 'modal--video';
            modalDialogClasses = 'modal-dialog-centered';
        }
        if (this._el.classList.contains(`${baseClass}--big-overlay`)) {
            this._overlayType = OVERLAY_TYPE_BIG;
        }
        if (this._toggleButton != null) {
            this._el.classList.add(`${baseClass}--clickable`);
        }

        this.attachEvents();
    }

    /**
     * Attaches general events to tile.
     */
    attachEvents() {
        if (this._toggleButton != null) {
            this._toggleButton.addEventListener('click', (event) => {
                this.isOpen = this.isOpen === false
                event.stopPropagation();
            });
            this._el.addEventListener('click', () => {
                if (this.isOpen) {
                    return;
                }

                this.isOpen = true;
            });
        }

        document.addEventListener(EVENT_TILE_OPEN, () => this.isOpen = false);
    }

    /**
     * Attaches special events to currently open tile to close it.
     */
    attachCloseEvents() {
        this._closeButton?.addEventListener('click', this.handleCloseClick.bind(this), {
            passive: true,
        });

        this._overlay?.addEventListener('hidden.bs.modal', this.handleModalClose.bind(this), {
            passive: true,
        });

        const anchorLinks = this._overlay.querySelectorAll('.close-modal');
        const self = this;
        for (let link of anchorLinks) {
            link.addEventListener('click', function(){
                self._closeButton.click();
            })
        }
    }

    /**
     * Handles a click on a previously created close button.
     */
    handleCloseClick() {
        this._overlayModal?.hide();
        this._closeButton?.removeEventListener('click', this.handleCloseClick.bind(this));
    }

    /**
     * Handles closing a modal after it has been closed completely. Necessary, because otherwise ESC
     * is not handled correctly.
     */
    handleModalClose() {
        this._overlay?.removeEventListener('hidden.bs.modal', this.handleModalClose.bind(this));

        this.isOpen = false;
    }

    /**
     * Get the content that should be visible inside the overlay.
     *
     * @returns {HTMLElement|null}
     */
    getOverlayTemplate() {
        if (this._overlayTemplate == null) {
            return null;
        }

        return /** @type {HTMLElement} **/ this._overlayTemplate.content.cloneNode(true);
    }

    /**
     * Shows the overlay for the current
     */
    showOverlay() {
        if (this._overlay != null) {
            return;
        }

        const template = this.getOverlayTemplate();
        if (template == null) {
            console.warn('Could not find overlay template. Element: ', this._el);

            return;
        }

        document.dispatchEvent(new CustomEvent(EVENT_TILE_OPEN));

        switch (this._overlayType) {
            case OVERLAY_TYPE_BIG:
                this.showBigOverlay(template);

                break;

            default:
                this.showSmallOverlay(template);

                break;
        }

        this._el.classList.add(`${baseClass}--open`);

        dispatchLayoutChange();
    }

    /**
     * Creates and shows the small overlay. The small overlay is just visible inside the tile.
     *
     * @param {HTMLElement} template
     */
    showSmallOverlay(template) {
        this._overlay = this.createSmallOverlay(template);
        this._el.appendChild(this._overlay);

        this.isBodyVisible = false;

        this._el.classList.add(`${baseClass}--open-inline`);

        // Timeout to allow browser to create element first and then apply animation.
        setTimeout(() => {
            this._el.classList.add(`${baseClass}--overlay-open`);
        }, 100);
    }

    /**
     * Creates and shows the big overlay. This overlay is inside a bootstrap modal.
     *
     * @param {HTMLElement} template
     */
    showBigOverlay(template) {
        ({
            /** @type {HTMLElement} */ overlay: this._overlay,
            /** @type {HTMLElement} */ closeButton: this._closeButton
        } = this.createBigOverlay(template));

        document.body.append(this._overlay);

        this._overlayModal = /** @type {Modal} */ Modal.getOrCreateInstance(this._overlay, {
            backdrop: 'fix', // Any value other than true or 'static' would hide the modal on click or show ugly animation.
        });
        this._overlayModal.show();

        enableBlurBackdrop();
        this.attachCloseEvents();
    }

    /**
     * Hides any currently open overlay.
     */
    hideOverlay() {
        this._el.classList.remove(...[
            `${baseClass}--overlay-open`,
            `${baseClass}--open`
        ]);

        switch (this._overlayType) {
            case OVERLAY_TYPE_BIG:
                return this.hideBigOverlay();
            default:
                return this.hideSmallOverlay();
        }
    }

    /**
     * Hides the open big overlay and clears all references.
     */
    hideBigOverlay() {
        this._overlayModal.hide();
        this._overlay.remove();

        this._overlayModal = null;
        this._overlay = null;
    }

    /**
     * Hides the small overlay and removes all references.
     */
    hideSmallOverlay() {
        this.isBodyVisible = true;

        if (this._overlay == null) {
            return;
        }

        this._el.classList.remove(`${baseClass}--open-inline`);

        this._overlay.remove();
        this._overlay = null;
    }

    /**
     * Builds the markup for the small overlay to be drawn inside the tile.
     *
     * @param {HTMLElement} innerElement
     * @returns {HTMLElement}
     */
    createSmallOverlay(innerElement) {
        const wrapper = document.createElement('div');
        wrapper.classList.add(`${baseClass}__overlay`);
        wrapper.appendChild(innerElement);

        return wrapper;
    }

    /**
     * Builds the markup for the big overlay / modal to be drawn below the tile.
     *
     * @param {HTMLElement} innerElement
     * @returns {{ overlay: HTMLElement, closeButton: HTMLElement }}
     */
    createBigOverlay(innerElement) {
        const wrapper = document.createElement('div');
        wrapper.classList.add(...['modal', 'modal--blur']);
        if (modalCustomClasses !== '') {
            wrapper.classList.add(...[modalCustomClasses]);
        }
        wrapper.tabIndex = -1;
        const closeButton = document.createElement('div');
        closeButton.classList.add(...['modal-close-button', 'modal-close-button--rotate']);
        closeButton.innerHTML = `
            <button type="button" aria-label="Modal schließen">
                <svg viewBox="0 0 35 35" xmlns="http://www.w3.org/2000/svg"><circle cx="17.5" cy="17.5" fill="none" r="17.5"/><g><path d="m17.5 35a17.5 17.5 0 1 1 17.5-17.5 17.52 17.52 0 0 1 -17.5 17.5zm0-34a16.5 16.5 0 1 0 16.5 16.5 16.52 16.52 0 0 0 -16.5-16.5z"/><path d="m27.5 17h-9.5v-9.5h-1v9.5h-9.5v1h9.5v9.5h1v-9.5h9.5z"/></g></svg>
            </button>
        `;

        const modalDialog = document.createElement('div');
        modalDialog.classList.add(...['modal-dialog', 'modal-fullscreen', 'modal-dialog--custom']);
        if (modalCustomClasses !== '') {
            wrapper.classList.add(...[modalCustomClasses]);
        }
        modalDialog.append(innerElement);

        wrapper.append(closeButton);
        wrapper.append(modalDialog);

        return {
            overlay: wrapper,
            closeButton: closeButton,
        };
    }

    /**
     * @return {boolean}
     */
    get isOpen() {
        return this._isOpen;
    }

    /**
     * @param {boolean} value
     */
    set isOpen(value) {
        if (this._isOpen === value) {
            return;
        }

        if (value === false) {
            this.hideOverlay();
            this._isOpen = false;

            return;
        }

        this.showOverlay();
        this._isOpen = true;
    }

    /**
     * @param {Boolean} value
     */
    set isBodyVisible(value) {
        const bodies = this._el.getElementsByClassName(`${baseClass}__body`);

        for (const body of bodies) {
            body.setAttribute('aria-hidden', (value === false).toString());
        }
    }
}

export default function initializeTiles() {
    const tiles = document.getElementsByClassName('tile');

    for (const tile of tiles) {
        if (!tile.classList.contains('tile--video')) {
            tryExecution(() => new Tile(tile));
        }
    }
}
